import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { useParams } from "react-router";
import {
  adminActions,
  adminSelectors,
  authSelectors,
  useDispatch,
  useSelector,
} from "../../../../state";
import {
  homepageButtonOptions,
  resolveHomepageButtons,
} from "../../../../config/homepageButtons";
import { useStyles } from "../AgencySettingsPage.styles";

const HomepageButtonsCard = () => {
  const classes = useStyles();
  const dispatch = useDispatch<any>();
  const { id } = useParams<{ id?: string }>();
  const activeAgencyId = useSelector(authSelectors.activeAgencyId);
  const agencyId = id ? Number(id) : activeAgencyId;
  const isSuperAdmin = useSelector(authSelectors.isSuperAdmin);
  const agency = useSelector(adminSelectors.activeAgencyData);
  const [draft, setDraft] = useState(() => resolveHomepageButtons(null));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const saveContext = useRef(0);

  useEffect(() => {
    saveContext.current += 1;
    setEditing(false);
    setFailed(false);
    setSaving(false);
    if (agencyId && isSuperAdmin) dispatch(adminActions.getAgency(agencyId));
    return () => {
      // An old save must not change the next agency's editor or an unmounted card.
      saveContext.current += 1;
    };
  }, [agencyId, isSuperAdmin, dispatch]);

  if (!isSuperAdmin || !agencyId || agency?.id !== agencyId) return null;

  const startEditing = () => {
    setDraft(resolveHomepageButtons(agency.homepage_buttons));
    setFailed(false);
    setEditing(true);
  };
  const save = async () => {
    const context = saveContext.current;
    setSaving(true);
    setFailed(false);
    try {
      const saved = await dispatch(
        adminActions.updateAgencyHomepageButtons(agencyId, draft),
      );
      if (context !== saveContext.current) return;
      if (saved) setEditing(false);
      else setFailed(true);
    } finally {
      if (context === saveContext.current) setSaving(false);
    }
  };
  const values = editing
    ? draft
    : resolveHomepageButtons(agency.homepage_buttons);

  return (
    <Card variant="outlined" className={classes.section}>
      <Box className={classes.sectionTitleWrapper}>
        <Typography className={classes.sectionTitle}>
          Homepage buttons
        </Typography>
        {!editing && <Button onClick={startEditing}>Edit</Button>}
      </Box>
      <Typography variant="body2" sx={{ my: 2 }}>
        Choose which actions appear on this agency’s homepage. Only super admins
        can change these settings.
      </Typography>
      {homepageButtonOptions.map(({ key, label }) => (
        <FormControlLabel
          key={key}
          label={label}
          control={
            <Switch
              checked={values[key] === true}
              disabled={!editing || saving}
              onChange={(_, checked) =>
                setDraft((previous) => ({ ...previous, [key]: checked }))
              }
            />
          }
        />
      ))}
      {editing && !Object.values(draft).some(Boolean) && (
        <Typography variant="body2" sx={{ my: 2 }}>
          No action buttons will appear. Visitors will see a message inviting
          them to contact the agency.
        </Typography>
      )}
      {failed && (
        <Typography role="alert" color="error">
          Could not save. Your changes are still here; please try again.
        </Typography>
      )}
      {editing && (
        <Box className={classes.submit}>
          <Button
            disabled={saving}
            onClick={() => {
              setEditing(false);
              setFailed(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default HomepageButtonsCard;
